import React from 'react';

const withdrawThemeVariableEntries = [
  [
    '--withdraw-primary-gradient',
    'linear-gradient(rgb(0, 181, 18), rgb(0, 145, 14))',
  ],
  ['--withdraw-coin-selected-background', 'rgba(0, 181, 18, 0.1)'],
] as const;

export const withdrawThemeStyle = Object.fromEntries(
  withdrawThemeVariableEntries,
) as React.CSSProperties;

export function useWithdrawThemeVariables() {
  React.useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    const rootStyle = document.documentElement.style;
    const previousValues = withdrawThemeVariableEntries.map(
      ([name]) => [name, rootStyle.getPropertyValue(name)] as const,
    );

    withdrawThemeVariableEntries.forEach(([name, value]) => {
      rootStyle.setProperty(name, value);
    });

    return () => {
      previousValues.forEach(([name, value]) => {
        if (value) {
          rootStyle.setProperty(name, value);
        } else {
          rootStyle.removeProperty(name);
        }
      });
    };
  }, []);
}

const WITHDRAW_PROGRESSIVE_VIEWPORT_HEIGHT_VAR =
  '--withdraw-progressive-viewport-height';

export function useWithdrawProgressiveViewportHeightStyle() {
  React.useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    const updateViewportHeight = () => {
      const height = window.visualViewport?.height ?? window.innerHeight;
      document.documentElement.style.setProperty(
        WITHDRAW_PROGRESSIVE_VIEWPORT_HEIGHT_VAR,
        `${height}px`,
      );
    };

    updateViewportHeight();
    window.addEventListener('resize', updateViewportHeight);
    window.visualViewport?.addEventListener('resize', updateViewportHeight);
    window.visualViewport?.addEventListener('scroll', updateViewportHeight);

    return () => {
      window.removeEventListener('resize', updateViewportHeight);
      window.visualViewport?.removeEventListener(
        'resize',
        updateViewportHeight,
      );
      window.visualViewport?.removeEventListener(
        'scroll',
        updateViewportHeight,
      );
      document.documentElement.style.removeProperty(
        WITHDRAW_PROGRESSIVE_VIEWPORT_HEIGHT_VAR,
      );
    };
  }, []);

  return React.useMemo(
    () => ({
      height: `var(${WITHDRAW_PROGRESSIVE_VIEWPORT_HEIGHT_VAR}, 100vh)`,
    }),
    [],
  );
}
